import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { GuildType, GuildVisibility } from '@/lib/api';

export function GuildCreatePage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [guildType, setGuildType] = useState<GuildType>('regular');
  const [visibility, setVisibility] = useState<GuildVisibility>('public');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const guild = await api.createGuild({
        name: name.trim(),
        description: description.trim() || undefined,
        guildType,
        visibility,
      });
      navigate(`/a/arche_guilds/${guild.guildId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create guild');
      setCreating(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Link
        to="/a/arche_guilds"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Guilds
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>Create a Guild</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Awesome Guild"
                maxLength={100}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this guild about?"
                maxLength={2000}
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={guildType} onValueChange={(v) => setGuildType(v as GuildType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="earning">Earning</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {guildType === 'earning'
                    ? 'Guild can earn and distribute funds'
                    : 'Standard collaborative guild'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Visibility</Label>
                <Select
                  value={visibility}
                  onValueChange={(v) => setVisibility(v as GuildVisibility)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {visibility === 'private' ? 'Invite only' : 'Anyone can join'}
                </p>
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button type="submit" disabled={creating} className="w-full">
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Guild
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
